import {
  companyName,
  normalizeDigits,
  resolvePolicyTokens,
  textRows,
} from "@1-now/booking-core";
import { buildContactRows } from "utils/contactRows";
import { formatPhone, phoneHref } from "utils/formatPhone";

/**
 * 1NOW-11114: policy text with every placeholder filled in. The package
 * resolves {{phone}}, {{email}}, {{address}} and {{deposit amount}}; the
 * seeded default policies also carry {{company name}}, resolved here so all
 * four policy surfaces (policy pages, checkout modals) use one function.
 * A function replacer inserts the name literally ("$&" in a name stays put).
 *
 * The package reads phone, email and address from the company record, and
 * plenty of real companies (PM Auto, 2008) keep none of them there while the
 * footer a renter sees carries all three. Those fall back to the footer's own
 * rows (see policyContacts). Whatever is still unknown is printed as nothing,
 * and the words that led to it go too: a "Phone:" item with no phone is not
 * listed, and "Questions: {{email}} or {{phone}}." never prints "Questions: or ."
 */

/** The footer rows a renter already sees, for a company record that has no contact details. */
export interface PolicyFooter {
  /** The footer's `contact_items` keypoints, as the provider holds them. */
  contactItems?: unknown;
  /** The footer's `address_items` keypoints. */
  addressItems?: unknown;
}

const has = (v: unknown) => String(v ?? "").trim() !== "";
const escapeHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const escapeAttr = (s: string) => escapeHtml(s).replace(/"/g, "&quot;");

/**
 * What {{phone}}, {{email}} and {{address}} stand for: the company record's own
 * value when it has one, else the first matching footer row, else (address
 * only) the website details'. A footer phone is read from the number its row
 * dials, never from its label ("Call us"); a WhatsApp row counts as a phone.
 */
export const policyContacts = (ourCompany: any, companyDetails?: any, footer?: PolicyFooter) => {
  const company = ourCompany?.company ?? {};
  const rows = buildContactRows(footer?.contactItems);
  const dialled = rows
    .filter((r) => r.type === "phone" || r.type === "whatsapp")
    .map((r) => normalizeDigits(r.href))
    .find((digits) => digits.length >= 7);
  const mailed = rows
    .filter((r) => r.type === "email")
    .map((r) => r.href.replace(/^mailto:/i, "").split("?")[0].trim())
    .find((email) => email.includes("@"));
  const line = textRows(footer?.addressItems)
    .map((r) => (r.description || r.heading).trim())
    .find(Boolean);
  return {
    phone: has(company.phone_no) ? String(company.phone_no) : dialled ? `+${dialled}` : "",
    email: has(company.email) ? String(company.email) : mailed ?? "",
    address: has(company.address)
      ? String(company.address)
      : line ?? String(companyDetails?.address ?? "").trim(),
  };
};

/**
 * {{phone}} and {{email}} as links: the number the renter reads, dialled by a tel: link built the
 * way the footer builds it, and the address, mailed by a mailto: link. A token inside a link the
 * operator already wrote (href="mailto:{{email}}") is left to the plain pass, and a value that
 * cannot be dialled or mailed stays a token, which the pass after this one fills or drops. The
 * stored text is never changed; this runs on what is rendered.
 */
const linkContacts = (html: string, contacts: { phone: string; email: string }): string => {
  const tel = phoneHref(contacts.phone);
  const phone = formatPhone(contacts.phone);
  const mail = contacts.email.trim();
  return html.replace(/(<a\b[\s\S]*?<\/a>)|\{\{(phone|email)\}\}/gi, (match, anchor, token) => {
    if (anchor) return match;
    if (token.toLowerCase() === "phone")
      return tel && phone ? `<a href="${escapeAttr(tel)}">${escapeHtml(phone)}</a>` : match;
    return /^[^\s@]+@[^\s@]+$/.test(mail) ? `<a href="mailto:${escapeAttr(mail)}">${escapeHtml(mail)}</a>` : match;
  });
};

/** Stands for a placeholder that resolved to nothing, until the text around it is tidied. */
const HOLE = "";

/** What a missing value leaves behind: its list item or line, and the "or" / "at" / ":" that led to it. */
const tidyHoles = (html: string): string => {
  if (!html.includes(HOLE)) return html;
  // "<li>Phone: </li>" says nothing: a label, and nothing after it
  const labelOnly = (inner: string) => {
    const text = inner.replace(/<[^>]*>/g, "").split(HOLE).join("").replace(/&nbsp;/g, " ").trim();
    return text === "" || /^[^:]{1,40}:$/.test(text);
  };
  return (
    html
      .replace(/<(li|p)\b[^>]*>([\s\S]*?)<\/\1>\s*/gi, (item, _tag, inner: string) =>
        inner.includes(HOLE) && labelOnly(inner) ? "" : item,
      )
      // a list emptied that way goes, and so does the colon that introduced it
      .replace(/:(\s*(?:<\/[a-z][a-z0-9]*>\s*)*<\/p>)\s*<(ul|ol)\b[^>]*>\s*<\/\2>/gi, ".$1")
      .replace(/<(ul|ol)\b[^>]*>\s*<\/\1>/gi, "")
      // "<hole> or x" keeps x; "of <hole>." and ": <hole>." go with their hole
      .replace(new RegExp(`${HOLE}\\s*(?:or|and)\\s+`, "gi"), "")
      .replace(new RegExp(`\\s*(?:\\b(?:of|at|on|via|by|to|or|and|is)\\b|:)\\s*${HOLE}`, "gi"), "")
      .replace(new RegExp(`\\s*${HOLE}`, "g"), "")
      .replace(/<a\b[^>]*>\s*<\/a>/gi, "")
  );
};

export const resolvePolicyText = (
  html: string | null | undefined,
  ourCompany: any,
  companyDetails?: any,
  footer?: PolicyFooter,
  /** links: {{phone}} and {{email}} come out as tel: and mailto: links, for a page that renders the text as html. */
  opts?: { links?: boolean },
): string => {
  const own = ourCompany?.company ?? {};
  const contacts = policyContacts(ourCompany, companyDetails, footer);
  // The package fills these from the company record, so the fallbacks go there.
  // Only the fallbacks are escaped: they are the operator's footer text.
  const company = {
    ...ourCompany,
    company: {
      ...own,
      // The package prints a non-US number as it is stored; formatted here, it passes through.
      phone_no: formatPhone(contacts.phone),
      email: has(own.email) ? own.email : escapeHtml(contacts.email),
      address: has(own.address) ? own.address : escapeHtml(contacts.address),
    },
  };
  const name = companyName(companyDetails, ourCompany);
  const marked = ["phone", "email", "address", "deposit amount"].reduce(
    (text, token) =>
      resolvePolicyTokens(`{{${token}}}`, company).trim()
        ? text
        : text.split(`{{${token}}}`).join(HOLE),
    (opts?.links ? linkContacts(String(html ?? ""), contacts) : String(html ?? "")).replace(
      /\{\{company name\}\}/g,
      () => name || HOLE,
    ),
  );
  return tidyHoles(resolvePolicyTokens(marked, company));
};
